/**
 * CmWriteNoteForm — BottomSheet form for writing a new CM Note on an
 * organization (Block 6.F.4 §3.4; TypeScript on the primitives per UX-13
 * S5 — the footer buttons compose <Button>, retiring the raw BTN_* pair
 * and taking this file off the raw-button allowlist).
 *
 * Architecture:
 *   - Pages render a button "Add a note" at the BOTTOM of the CM Notes
 *     section. The button toggles this form's `isOpen` prop.
 *   - Form lives inside BottomSheet primitive (peace-hub-port shape;
 *     second consumer after FilterBar in 6.F.2).
 *   - Submit calls `api.createProviderNote(slug, providerId, body)`;
 *     handler at api/src/functions/providers.ts:390-446 routes through
 *     repo at api/_shared/repos/providerNotes.ts:155+ (BEFORE INSERT
 *     trigger assigns version per migration 0020:71-91).
 *
 * Field shape:
 *   - body — required, multiline textarea, "What's on your mind about
 *     [Org Name]?" placeholder
 *   - note_type — radio buttons (default 'general' per migration 0055
 *     CHECK constraint). Icons match badge colors per design plan §3.7:
 *     💡 Tip / ⚠️ Warning / 📝 General.
 *   - is_anonymous — checkbox + tooltip explaining display-only
 *     suppression per ADR-011 §5.
 *
 * UX sensibility per design plan §3.4 + §3.7:
 *   - "Share note" submit button (warmer than "Submit")
 *   - Anonymity tooltip: "Your name won't show next to this note. The
 *     system still records who wrote it for safety." — honest about
 *     surveillance-but-not-display tradeoff
 *
 * Per ADR-024 D-affiliation-editor-shape: per-form Save earned by
 * multi-field coherence (body + type + anonymity form one logical
 * note) + inter-field validation (body required) + undo-as-one-logical-
 * unit (provider_notes is append-only versioned per ADR-011 §5; no
 * undo for note creation — submitting IS the commit).
 *
 * Phase 1.14 — optional pre-population for "edit a note" flow.
 * Phase 1.14.2 — when `editNoteId` is set, submit calls
 * api.updateProviderNote (PATCH) instead of api.createProviderNote
 * (POST). The row UPDATEs in place; structural columns
 * (id/author_user_id/created_at/version) are preserved by the server.
 * Without this prop, Phase 1.14's edit path would INSERT a duplicate
 * row alongside the original (the data-steward-anchored bug).
 *
 * When `initialBody` is provided, the form opens in "edit mode": the
 * BottomSheet title flips to "Edit a note" and the submit button to
 * "Save edit". The reset-on-open useEffect re-syncs state when the
 * parent opens the form with a NEW set of initial values (BottomSheet
 * unmounts children on close, so first-open init is already fresh; the
 * effect covers "open → close → reopen with different initials"). See
 * reference_bottomsheet_state_lifecycle.md.
 *
 * `noteTarget` says whose notes these are: 'organization' (the default)
 * writes through the provider note calls, 'individual' through the
 * Directory person's (api.createIndividualNote / updateIndividualNote),
 * with `providerId` carrying the person's id. Everything else is the same
 * form.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { invalidateAdminReviewTimelineOnly } from '@/lib/adminReview';
import { pageWriteText, serverSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { BottomSheet } from '@/components/primitives/BottomSheet';
import { Button } from '@/components/primitives/Button';
import { TextArea } from '@/components/primitives/TextInput';
import {
  FLD_TEXTAREA,
  FLD_LABEL,
  MODAL_FOOT,
  CHECKROW,
  ModalCloseButton,
} from '@/components/primitives/modalFormStyles';
import { refreshFamily } from '@/lib/refreshFamily';
import type { TeamNote } from '@/lib/types';

export type NoteType = 'tip' | 'warning' | 'general';

const TYPE_OPTIONS: ReadonlyArray<{ value: NoteType; icon: string; label: string; description: string }> = [
  { value: 'tip', icon: '💡', label: 'Tip', description: 'Something useful to share' },
  { value: 'warning', icon: '⚠️', label: 'Warning', description: 'Heads-up for fellow CMs' },
  { value: 'general', icon: '📝', label: 'General', description: 'Anything else' },
];

export interface CmWriteNoteFormProps {
  isOpen: boolean;
  onClose: () => void;
  tenantSlug: string;
  /** The listing's id: the organization's, or the person's when noteTarget is 'individual'. */
  providerId: string;
  /** Whose notes: an organization's (default) or a Directory person's. */
  noteTarget?: 'organization' | 'individual';
  providerName: string;
  invalidationKey?: ReadonlyArray<unknown>;
  initialBody?: string;
  initialNoteType?: NoteType;
  initialIsAnonymous?: boolean;
  editNoteId?: string;
}

export function CmWriteNoteForm({
  isOpen,
  onClose,
  tenantSlug,
  providerId,
  noteTarget = 'organization',
  providerName,
  invalidationKey,
  initialBody,
  initialNoteType,
  initialIsAnonymous,
  editNoteId,
}: CmWriteNoteFormProps): ReactNode {
  const queryClient = useQueryClient();
  const isEditMode = initialBody !== undefined;
  const [body, setBody] = useState(initialBody ?? '');
  const [noteType, setNoteType] = useState<NoteType>(initialNoteType ?? 'general');
  const [isAnonymous, setIsAnonymous] = useState(initialIsAnonymous ?? false);
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();

  // Re-sync state when the form is re-opened with a new set of initial
  // values (e.g. user clicks the pencil on note B while a stale edit-form
  // for note A is in memory between opens). Runs on isOpen flipping true
  // or on any initial-value change while open.
  useEffect(() => {
    if (isOpen) {
      setBody(initialBody ?? '');
      setNoteType(initialNoteType ?? 'general');
      setIsAnonymous(initialIsAnonymous ?? false);
      setError(null);
    }
  }, [isOpen, initialBody, initialNoteType, initialIsAnonymous]);

  function handleClose() {
    // Reset form on close — fresh slate next time. (When the form re-opens
    // in edit mode, the useEffect above re-applies the initials.)
    setBody('');
    setNoteType('general');
    setIsAnonymous(false);
    setError(null);
    onClose();
  }

  const mutation = useMutation({
    // Phase 1.14.2 — when editNoteId is provided, PATCH-in-place
    // instead of POST-create. Same body shape on the wire.
    // author_user_id + created_at + version stay with the original
    // creator/row; only body/note_type/is_anonymous can change.
    // The note as typed and the list to refresh (the family) are read at
    // the press and travel as the variables.
    // Whose note it is (an organization's or a person's) travels with them.
    mutationFn: (v: { note: { body: string; is_anonymous: boolean; note_type: typeof noteType }; family: readonly unknown[] | null; target: 'organization' | 'individual' }): Promise<TeamNote> =>
      v.target === 'individual'
        ? editNoteId
          ? api.updateIndividualNote(tenantSlug, providerId, editNoteId, v.note)
          : api.createIndividualNote(tenantSlug, providerId, v.note)
        : editNoteId
          ? api.updateProviderNote(tenantSlug, providerId, editNoteId, v.note)
          : api.createProviderNote(tenantSlug, providerId, v.note),
    onSuccess: async (_res, v) => {
      if (v.family !== null) {
        await queryClient.invalidateQueries({ queryKey: [...v.family] });
      }
      // Block 6.F.5 §7.8: note creation surfaces in the admin-review
      // timeline UNION (provider_notes branch). Note creation does NOT
      // change pending-suggestions count; timeline-only invalidation.
      await invalidateAdminReviewTimelineOnly(queryClient);
      handleClose();
    },
    onError: (e: Error) => {
      // A refused edit of someone else's note says so in the server's one
      // sentence; anything else is the shared save sentence.
      setError(serverSentence(e, 'directory_notes') ?? pageWriteText(e, contact));
    },
  });

  function handleSubmit() {
    setError(null);
    if (body.trim().length === 0) {
      setError('Add a few words before sharing.');
      return;
    }
    mutation.mutate({
      note: { body: body.trim(), is_anonymous: isAnonymous, note_type: noteType },
      family: invalidationKey ? refreshFamily(invalidationKey) : null,
      target: noteTarget,
    });
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit a note' : 'Add a note'}
      subtitle={providerName}
      headerRight={<ModalCloseButton onClick={handleClose} />}
      centered
    >
      <div className="px-[26px] py-[22px] space-y-5">
        <div>
          <label className={FLD_LABEL} htmlFor="cm-note-body">
            What&apos;s on your mind?
          </label>
          <TextArea
            id="cm-note-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            autoFocus
            placeholder={`What's on your mind about ${providerName}?`}
            aria-label="Note body"
            disabled={mutation.isPending}
            className={FLD_TEXTAREA}
          />
        </div>

        <fieldset>
          <legend className={FLD_LABEL}>Note type</legend>
          <div className="space-y-0.5">
            {TYPE_OPTIONS.map((opt) => (
              <label key={opt.value} className={CHECKROW}>
                <input
                  type="radio"
                  name="note_type"
                  value={opt.value}
                  checked={noteType === opt.value}
                  onChange={() => setNoteType(opt.value)}
                  disabled={mutation.isPending}
                  className="accent-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                />
                <span aria-hidden="true" className="text-base">
                  {opt.icon}
                </span>
                <span className="text-sm font-medium text-ink">{opt.label}</span>
                <span className="text-xs text-ink-soft">{opt.description}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className={`${CHECKROW} items-start`}>
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            disabled={mutation.isPending}
            className="mt-0.5 accent-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          />
          <div>
            <span className="text-sm font-medium text-ink block">Post anonymously</span>
            <span className="text-xs text-ink-soft block leading-[1.45]">
              Your name won&apos;t show next to this note. The system still records who wrote it for safety.
            </span>
          </div>
        </label>

        {error !== null && (
          <div
            role="alert"
            className="text-sm text-warm-700 bg-warm-50 border border-warm-200 rounded-xl px-3 py-2"
            data-testid="cm-note-error"
          >
            {error}
          </div>
        )}
      </div>

      <div className={MODAL_FOOT}>
        <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={mutation.isPending || body.trim().length === 0}
        >
          {mutation.isPending
            ? isEditMode
              ? 'Saving…'
              : 'Sharing…'
            : isEditMode
              ? 'Save edit'
              : 'Share note'}
        </Button>
      </div>
    </BottomSheet>
  );
}
